import { Box, Tooltip, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { getApiKeys } from 'entities/ApiKeys/api';
import { ApiKey } from 'entities/ApiKeys/types';
import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ColorPalette } from 'shared/consts/colorPalette';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { Button } from 'shared/ui/Button';
import { EmptyTable } from 'shared/ui/EmptyTable';
import { Icon } from 'shared/ui/Icon';
import { PageWrapper } from 'pages/_layouts/PageWrapper';
import { TableWithHeader } from 'shared/ui/tables/TableWithHeader';
import { ApiKeysColumns } from './consts/columns/publicApiColumns';
import CreateApiKeyModal from './partials/CreateApiKeyModal';
import DeleteApiKeyModal from './partials/DeleteApiKeyModal';

const MAX_KEYS = 10;

const PublicApiAccess: FC = () => {
	const { t } = useTranslation();
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null);

	const { data, refetch } = useQuery({
		queryKey: ['get/apiKeys'],
		queryFn: getApiKeys,
		onError: (err: AxiosError) => {
			showErrorNotification(err);
		},
	});

	const apiKeys = data?.data ?? [];
	const isLimitReached = apiKeys.length >= MAX_KEYS;

	const rows = useMemo(
		() =>
			apiKeys.map((key) => ({
				...key,
				id: key._id,
				onDelete: (k: ApiKey) => setKeyToDelete(k),
			})),
		[apiKeys],
	);

	return (
		<PageWrapper>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '24px',
				}}
			>
				<Typography variant={'h5'} color={ColorPalette.astronaut}>
					{t('public_api_keys.title')} ({apiKeys.length}/{MAX_KEYS})
				</Typography>
				<Tooltip title={isLimitReached ? t('public_api_keys.limit_tooltip') : ''} placement={'top'}>
					<span>
						<Button
							variant={'contained'}
							disabled={isLimitReached}
							onClick={() => setCreateModalOpen(true)}
							sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
						>
							<Icon
								id={'plus'}
								style={{ width: '16px', height: '16px', color: ColorPalette.white }}
							/>
							{t('public_api_keys.create_btn')}
						</Button>
					</span>
				</Tooltip>
			</Box>

			<Box
				sx={{
					backgroundColor: ColorPalette.catskillWhite,
					borderRadius: '12px',
					padding: '16px 20px',
					marginBottom: '28px',
				}}
			>
				<Typography
					variant={'body2'}
					color={ColorPalette.astronaut}
					fontWeight={600}
					sx={{ marginBottom: '6px' }}
				>
					{t('public_api_keys.info_title')}
				</Typography>
				<Typography variant={'body2'} color={ColorPalette.baliHai} sx={{ marginBottom: '6px' }}>
					{t('public_api_keys.info_description')}
				</Typography>
				{(t('public_api_keys.info_points', { returnObjects: true }) as string[]).map(
					(point: string, i: number) => (
						<Typography key={i} variant={'body2'} color={ColorPalette.baliHai}>
							• {point}
						</Typography>
					),
				)}
				<Typography
					variant={'body2'}
					color={ColorPalette.royalBlue}
					component={'a'}
					href={'https://retica.ai/retica-rest-api/'}
					target={'_blank'}
					rel={'noopener noreferrer'}
					sx={{ display: 'inline-block', marginTop: '10px', cursor: 'pointer' }}
				>
					{t('public_api_keys.view_docs')} →
				</Typography>
			</Box>

			{rows.length > 0 ? (
				<TableWithHeader columns={ApiKeysColumns} rows={rows} />
			) : (
				<EmptyTable text={t('public_api_keys.empty_title')} />
			)}

			<CreateApiKeyModal
				open={createModalOpen}
				onClose={() => setCreateModalOpen(false)}
				onSuccess={() => refetch()}
			/>
			<DeleteApiKeyModal
				apiKey={keyToDelete}
				onClose={() => setKeyToDelete(null)}
				onSuccess={() => refetch()}
			/>
		</PageWrapper>
	);
};

export default PublicApiAccess;
